import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import ExternalBrowserNotice from "../modules/components/ExternalBrowserNotice";
import Icon from "../modules/components/Icon";
import {
  DOCUMENT_SAVE_MESSAGES,
  PDF_MIME_TYPE,
  fetchPdfFile,
  getSaveMethod,
  reopenInExternalBrowser,
  saveFile,
  toPdfFileName,
} from "../modules/utils/documentDownload";

// No inline preview: Chrome on Android cannot render a PDF in an iframe, and
// iOS only draws its first page.
const DocumentViewerPage = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const streamUrl = params.get("_url") || "";
  const fileName = useMemo(() => toPdfFileName(params.get("_name"), params.get("_dl") || "document"), [params]);
  const saveMethod = useMemo(() => getSaveMethod(fileName, PDF_MIME_TYPE), [fileName]);
  const canSaveHere = saveMethod === "share" || saveMethod === "download";

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveStarted = useRef(false);

  useEffect(() => {
    // Browsers that cannot save the file only get the "open in browser" notice.
    if (!canSaveHere) return;

    if (!streamUrl || !localStorage.getItem("token")) {
      setError("ไม่พบลิงก์เอกสาร");
      return;
    }

    let cancelled = false;
    fetchPdfFile(streamUrl, fileName)
      .then(nextFile => {
        if (!cancelled) setFile(nextFile);
      })
      .catch(() => {
        if (!cancelled) setError("เปิดเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      });

    return () => {
      cancelled = true;
    };
  }, [canSaveHere, streamUrl, fileName]);

  const handleSave = useCallback(async () => {
    if (!file) return;

    setIsSaving(true);
    setNotice("");
    try {
      const outcome = await saveFile({
        fileName,
        mimeType: PDF_MIME_TYPE,
        getFile: () => file,
        openExternal: reopenInExternalBrowser,
      });
      setNotice(DOCUMENT_SAVE_MESSAGES[outcome]);
    } catch (saveError) {
      console.error("Error saving document:", saveError);
      setNotice("บันทึกไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsSaving(false);
    }
  }, [file, fileName]);

  // Android browsers download without a tap, finishing what the user started in
  // LINE. The iOS share sheet needs a real tap, so iOS waits for the button.
  useEffect(() => {
    if (saveMethod !== "download" || !file || autoSaveStarted.current) return;
    autoSaveStarted.current = true;
    handleSave();
  }, [saveMethod, file, handleSave]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        padding: "24px",
        background: "#F5F7F8",
        fontFamily: "Google Sans, sans-serif",
        textAlign: "center",
      }}
    >
      <Icon icon="file-pdf" style={{ fontSize: "48px", color: "#4263B5" }} />
      <div style={{ color: "#2C2C2C", fontSize: "16px", fontWeight: 700, maxWidth: "320px", wordBreak: "break-all" }}>
        {fileName}
      </div>

      {saveMethod === "external" || saveMethod === "manual" ? (
        <ExternalBrowserNotice method={saveMethod} />
      ) : error ? (
        <div style={{ color: "#2C2C2C", fontSize: "16px" }}>{error}</div>
      ) : !file ? (
        <>
          <ProgressSpinner style={{ width: "56px", height: "56px" }} strokeWidth="4" />
          <div style={{ color: "#2C2C2C", fontSize: "16px" }}>กำลังเตรียมไฟล์ PDF...</div>
        </>
      ) : (
        <>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              width: "100%",
              maxWidth: "320px",
              border: "none",
              borderRadius: "8px",
              padding: "12px 20px",
              background: "#4263B5",
              color: "#FFFFFF",
              fontSize: "14px",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: isSaving ? 0.6 : 1,
            }}
          >
            <Icon icon={saveMethod === "share" ? "share-from-square" : "download"} style={{ fontSize: "14px" }} />
            <span>
              {isSaving
                ? "กำลังบันทึก..."
                : saveMethod === "share" ? "บันทึกไฟล์ PDF" : "ดาวน์โหลด PDF"}
            </span>
          </button>

          <div style={{ color: "#6F6B7D", fontSize: "13px", maxWidth: "320px" }}>
            {notice || (saveMethod === "share"
              ? "แตะปุ่มแล้วเลือก \"บันทึกไปยังไฟล์\" (Save to Files)"
              : "หากไฟล์ไม่ถูกดาวน์โหลด ให้กดปุ่มอีกครั้ง")}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentViewerPage;
